import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import Utils from '../../util';
import { Unit } from '../anessa-library/anessa-library/projects/unit-library/src/public-api';
import { AnessaTranslationService } from '../translation/anessa-translation.service';
import { takeUntil } from 'rxjs/operators';
import { UserpilotReloader } from '../userpilot-reloader.component';
import { LooseObject } from '../../models/loose-object.model';

@Component({
  selector: 'anessa-table',
  templateUrl: './table.component.html',
  styleUrls: [
    './table.component.scss'
  ]
})

export class TableComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Input() data: any[] = [];
  @Input() dataChangeNotifier?: EventEmitter<boolean>;
  @Input() keys: string[] = ['name'];
  @Input() customFields: string[] | string[][] = [];
  @Input() isCustomFieldsMultiColumn: boolean = false;
  @Input() showHeader = false;
  @Input() showModal = false;
  @Input() isRemoveActive = true;
  @Input() isEditActive = true;
  @Input() isAdditionalActive = false;
  @Input() isDuplicateActive = false;

  /**
   * When there are multiple cols then,
   * the `flex-1` class may cause cells content not fit
   */
  @Input() disableFlex1 = false;
  @Input() customFieldsFontSize = 'inherit';

  /**
   * Show the edit button but disable it.
   */
  @Input() disableEdit = false;
  @Input() disableDuplicate = false;
  /**
   * Show the remove button but disable it.
   */
  @Input() disableRemove = false;
  @Input() handleAdds = false;
  @Input() confirmText = 'PROJECT.COMMON.YES';
  @Input() removeTooltipText = 'PROJECT.COMMON.ARE_YOU_SURE';
  @Input() noTableItemsText = 'PROJECT.COMMON.ADD_ITEMS';
  @Input() editOnRowClick = false;
  @Input() editTextAltFunc = false;
  @Input() additionalBtnLinkText = 'HELPER.TABLE.NOT_SET';
  @Input() errorItems = [];
  @Input() warningItems = [];
  @Input() loadingItems = [];
  iconToggler = false;
  togglerInterval: NodeJS.Timeout;
  /**
   *
   * @type {string}
   * Sets the styling of the table. Current available styles:
   *  - default
   *  - small
   *  - apply-fade
   */
  @Input() style = 'default';

  /**
   * Cell styling using latex syntax
   * example: '\l\c\r\' = borders, left align, center, right align
   * IMPORTANT: button cells not included
   * @type {string}
   */
  @Input() cellstyle = '';
  cellAlign: string[] = [];
  borders: boolean[] = [];

  /**
   * Allow one entry per row or two per row
   * @type {boolean}
   */
  @Input() multiColumn = false;

  /**
   * An array that corresponds to the table items to determine if they should be highlighted by the accent color.
   */
  @Input() highlightedItems: boolean[] = [];

  /**
   *
   * @type {string} Selects editing behaviour
   *  - external: emits a signal with the item index
   *  - under-row: takes html between tags to use as editing form expanded under the row
   *     Expects a <div class="under-row-content">
   *  - modal: uses the html between tags to display a popup with an editing form
   *     Expects a <div class="modal-content">
   *  - in-place: change displayed fields in place
   */
  @Input() editType = 'external';
  @Output() onAdditional = new EventEmitter<number>();
  @Output() onEdit: EventEmitter<number> = new EventEmitter();
  @Output() onEditText: EventEmitter<number> = new EventEmitter(); // literal click of edit text
  @Output() onRemove: EventEmitter<number> = new EventEmitter();
  @Output() onSave: EventEmitter<boolean> = new EventEmitter();
  @Output() onDuplicate: EventEmitter<any> = new EventEmitter();
  removeIndex = -1;


  @Input() editIndex = -1;
  private backupData = null;

  /**
   * An array of which keys in the main table content should be translated.
   */
  @Input() translateKeys: string[] | null = null;
  /**
   * Boolean that determines if the custom fields should be translated.
   */
  @Input() translateCustomFields = false;
  @Input() customFieldsAreUnits = false;

  onDestroyNotifier = new EventEmitter();
  /** trigger for translation pipe */
  trigger?: string;

  @Input()
  stripedRows: boolean = false;

  @Input()
  columnLength?: number;

  @Input()
  textAlign?: string;

  @Input()
  paddingRight?: string;


  constructor(public translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
    if (this.dataChangeNotifier) {
      this.dataChangeNotifier.pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(() => {
          this.setup();
        });
    }

    this.setup();
  }

  private setup() {
    let indx = 0;
    if (this.data != null && this.data.length > 0) {
      const len = Object.keys(this.data[0]).length;
      while (indx < len) {
        this.borders.push(false);
        indx = indx + 1;
      }
    }
    if (this.cellstyle === '') {
      for (const cell of this.keys) {
        this.cellAlign.push('left');
      }
      // there is one last border on the right of the last element
      this.borders.push(false);
    } else {
      this.parseStylingInfo();
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
    clearInterval(this.togglerInterval);
  }

  onRemoveClick(i: number) {
    if (this.disableRemove) return;
    this.removeIndex = i;
  }

  getColor(storageUsage: number) {
    if (storageUsage > 100) {
      return 'warn';
    } else if (storageUsage < 100) {
      return 'accent';
    } else {
      return 'primary';
    }
  }

  getElement(object: LooseObject, keyString: string) {
    let currentObject = object;
    const currentPath = keyString.split('.');
    while (currentPath.length > 0) {
      if (!currentObject.hasOwnProperty(currentPath[0])) {
        // console.error('Key not found: ' + currentPath[0]);
        return null;
      }
      currentObject = currentObject[currentPath[0]];
      currentPath.splice(0, 1);
    }
    return currentObject;
  }

  onRemoveConfirm(i: number) {
    // set index back to default
    this.removeIndex = -1;
    if (this.editIndex !== -1) {
      if (this.backupData != null) {
        this.data[this.editIndex] = this.backupData;
      }
      this.editIndex = -1;
    }

    // emit remove request
    this.onRemove.emit(i);
  }

  onRemoveCancel() {
    this.removeIndex = -1;
  }

  onAdditionalClick(i: number) {
    this.onAdditional.emit(i);
  }

  onDuplicateClick(i: number) {
    if (this.disableDuplicate) return;

    this.backupData = Utils.deepCopy(this.data[i]);

    this.editIndex = i;

    this.onDuplicate.emit({item: this.backupData, index: i});
  }

  onEditClick(i: number, rowClick: boolean) {
    if (this.disableEdit) return;
    if (rowClick && !this.editOnRowClick) {
      return;
    }
    this.editIndex = i;
    if (this.editType === 'in-place') {
      // back up row in case of cancel
      this.backupData = Utils.deepCopy(this.data[i]);
    }
    this.onEdit.emit(i);
  }

  onEditTextClick(i: number, rowClick: boolean) {
    if (this.disableEdit) return;
    if (rowClick && !this.editOnRowClick) {
      return;
    }
    this.editIndex = i;
    if (this.editType === 'in-place') {
      // back up row in case of cancel
      this.backupData = Utils.deepCopy(this.data[i]);
    }
    this.onEditText.emit(i);
  }

  onCancelClick(index: number) {
    this.data[index] = this.backupData;
    this.editIndex = -1;
  }

  onSaveClick() {
    this.editIndex = -1;
    this.onSave.emit(true);
  }

  parseStylingInfo() {
    // remove all spaces
    this.cellstyle = this.cellstyle.replace(' ', '');
    this.cellstyle = this.cellstyle.toLowerCase();
    const myArray = this.cellstyle.split('');
    let cellNumber = 0;
    for (const myChar of myArray) {
      if (myChar === '|') {
        this.borders[cellNumber] = true;
      } else {
        switch (myChar) {
          case 'l':
            this.cellAlign.push('left');
            cellNumber = cellNumber + 1;
            break;
          case 'r':
            this.cellAlign.push('right');
            cellNumber = cellNumber + 1;
            break;
          case 'c':
            this.cellAlign.push('center');
            cellNumber = cellNumber + 1;
            break;
          default:
            console.error('Styling parse error');
            return;
        }
      }
    }
  }

  getBorder(index: number) {
    if (this.borders[index]) {
      return 'solid 1px #b1c0c9';
    }
    return 'none';
  }


  isHighlighted(index: number) {
    return this.highlightedItems[index];
  }

  isTranslateKey(key: string): boolean {
    return !!this.translateKeys && this.translateKeys.includes(key);
  }

  processCustomFieldItem(item: string) {
    if (this.translateCustomFields) {
      item = this.translationService.get(item);
    }
    if (this.customFieldsAreUnits) {
      item = Unit.parseUnitDisplayName(item);
    }
    return item;
  }

  getRowsStripped(): boolean {
    return this.stripedRows;
  }

  getParentWidth(): string {
    return this.columnLength ? (100 * (this.columnLength) / (this.columnLength + 1) + '%') : 'auto';
  }

  getChildWidth(): string {
    return this.columnLength ? (100 / this.columnLength + '%') : 'auto';
  }

  getTextAlign(): string {
    return this.textAlign ? this.textAlign : 'center';
  }

  getPaddingRight(): string {
    return this.paddingRight ? this.paddingRight : '50px';
  }

  /**
   * Returns a boolean to determine if any warning or error icons should be active.
   * If there are no items that match data in the table this will still return true.
   * If this becomes a problem this should be refactored to be more robust and check that
   * the provided items match any of the table data.
   */
  iconsActive() {
    return this.warningItems?.length > 0 || this.errorItems?.length > 0;
  }

  /**
   * Returns a boolean that determines if the warning icon should be shown for the provided
   * data.
   * @param dataset is the dataset object.
   * @param key is the key of the specific data in the provided dataset.
   */
  showWarningIcon(dataset: LooseObject, key: string) {
    return this.warningItems?.includes(this.getElement(dataset, key));
  }

  /**
   * Returns a boolean that determines if the error icon should be shown for the provided
   * data.
   * @param dataset is the dataset object.
   * @param key is the key of the specific data in the provided dataset.
   */
  showErrorIcon(dataset: LooseObject, key: string) {
    return this.errorItems?.includes(this.getElement(dataset, key));
  }

  /**
   * Returns a boolean that determines if the loading spinner should be shown for the provided
   * data.
   * @param dataset is the dataset object.
   * @param key is the key of the specific data in the provided dataset.
   */
  showLoadingIcon(dataset: LooseObject, key: string) {
    return this.loadingItems?.includes(this.getElement(dataset, key));
  }

  /**
   * Returns a boolean that determines if both an error and warning icon should be shown for
   * the provided data.
   * @param dataset is the dataset object.
   * @param key is the key of the specific data in the provided dataset.
   */
  hasErrorAndWarning(dataset: LooseObject, key: string) {
    if (this.showErrorIcon(dataset, key) && this.showWarningIcon(dataset, key)) {
      this.startIconToggler();
      return true;
    }
  }

  /**
   * Returns a boolean that determines if either a warning or error icon will be shown. This is
   * used to activate the icon functionality for a specific dataset and key.
   * @param dataset is the dataset object.
   * @param key is the key of the specific data in the provided dataset.
   */
  showIconsForDataset(dataset: LooseObject, key: string) {
    return this.showErrorIcon(dataset, key) || this.showWarningIcon(dataset, key) || this.showLoadingIcon(dataset, key);
  }

  /**
   * This function sets the icon toggler interval unless it has already been created. This toggler interval
   * will be destroyed in the onDestroy method of this component.
   * @private
   */
  private startIconToggler() {
    if (!this.togglerInterval) {
      this.togglerInterval = setInterval(() => {
        this.iconToggler = !this.iconToggler;
      }, 1000);
    }
  }
}
